import { useRef, useCallback } from 'react';

export function useAudio() {
  const ctxRef = useRef(null);
  const masterGainRef = useRef(null);
  const ambientStartedRef = useRef(false);
  const mutedRef = useRef(false);

  const getCtx = useCallback(() => {
    if (!ctxRef.current) {
      ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (ctxRef.current.state === 'suspended') {
      ctxRef.current.resume();
    }
    return ctxRef.current;
  }, []);

  const startAmbient = useCallback(() => {
    if (ambientStartedRef.current) return;
    ambientStartedRef.current = true;

    const ctx = getCtx();

    const master = ctx.createGain();
    master.gain.setValueAtTime(mutedRef.current ? 0 : 0.32, ctx.currentTime);
    master.connect(ctx.destination);
    masterGainRef.current = master;

    // Base drone — low A2 (110 Hz)
    const d1 = ctx.createOscillator();
    d1.type = 'sine';
    d1.frequency.setValueAtTime(110, ctx.currentTime);
    const g1 = ctx.createGain();
    g1.gain.setValueAtTime(0.42, ctx.currentTime);
    d1.connect(g1); g1.connect(master); d1.start();

    // E3 (165 Hz) — warmth
    const d2 = ctx.createOscillator();
    d2.type = 'sine';
    d2.frequency.setValueAtTime(165, ctx.currentTime);
    const g2 = ctx.createGain();
    g2.gain.setValueAtTime(0.24, ctx.currentTime);
    d2.connect(g2); g2.connect(master); d2.start();

    // A3 (220 Hz) — melody layer with slow vibrato for Caribbean feel
    const d3 = ctx.createOscillator();
    d3.type = 'sine';
    d3.frequency.setValueAtTime(220, ctx.currentTime);
    const g3 = ctx.createGain();
    g3.gain.setValueAtTime(0.15, ctx.currentTime);
    d3.connect(g3); g3.connect(master); d3.start();

    // Slow melody LFO — very gentle pitch drift
    const mLFO = ctx.createOscillator();
    mLFO.type = 'sine';
    mLFO.frequency.setValueAtTime(0.12, ctx.currentTime);
    const mLFOGain = ctx.createGain();
    mLFOGain.gain.setValueAtTime(10, ctx.currentTime);
    mLFO.connect(mLFOGain);
    mLFOGain.connect(d3.frequency);
    mLFO.start();

    // Slow tremolo on g2 for warmth
    const tremLFO = ctx.createOscillator();
    tremLFO.type = 'sine';
    tremLFO.frequency.setValueAtTime(2.2, ctx.currentTime);
    const tremGain = ctx.createGain();
    tremGain.gain.setValueAtTime(0.055, ctx.currentTime);
    tremLFO.connect(tremGain);
    tremGain.connect(g2.gain);
    tremLFO.start();

    // Room noise — very quiet
    const bufLen = Math.floor(ctx.sampleRate * 3);
    const noiseBuf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
    const nd = noiseBuf.getChannelData(0);
    for (let i = 0; i < bufLen; i++) nd[i] = (Math.random() * 2 - 1) * 0.5;
    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuf; noise.loop = true;
    const nf = ctx.createBiquadFilter();
    nf.type = 'bandpass';
    nf.frequency.setValueAtTime(280, ctx.currentTime);
    nf.Q.setValueAtTime(0.4, ctx.currentTime);
    const ng = ctx.createGain();
    ng.gain.setValueAtTime(0.014, ctx.currentTime);
    noise.connect(nf); nf.connect(ng); ng.connect(master);
    noise.start();
  }, [getCtx]);

  // Soft Caribbean pentatonic pluck for feather placement
  const playFeatherPlace = useCallback(() => {
    const ctx = getCtx();
    if (mutedRef.current) return;

    // A pentatonic notes (steel-pan-like)
    const pentatonic = [440, 493.88, 554.37, 659.25, 739.99];
    const freq = pentatonic[Math.floor(Math.random() * pentatonic.length)];

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, ctx.currentTime);
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.22, ctx.currentTime + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.1);
    osc.connect(gain); gain.connect(ctx.destination);
    osc.start(); osc.stop(ctx.currentTime + 1.1);

    // Soft second harmonic
    const osc2 = ctx.createOscillator();
    const g2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(freq * 2, ctx.currentTime);
    g2.gain.setValueAtTime(0.07, ctx.currentTime);
    g2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.55);
    osc2.connect(g2); g2.connect(ctx.destination);
    osc2.start(); osc2.stop(ctx.currentTime + 0.55);
  }, [getCtx]);

  const dipAmbient = useCallback(() => {
    if (!masterGainRef.current || mutedRef.current) return;
    const ctx = getCtx();
    masterGainRef.current.gain.cancelScheduledValues(ctx.currentTime);
    masterGainRef.current.gain.linearRampToValueAtTime(0.035, ctx.currentTime + 0.9);
  }, [getCtx]);

  const restoreAmbient = useCallback(() => {
    if (!masterGainRef.current || mutedRef.current) return;
    const ctx = getCtx();
    masterGainRef.current.gain.cancelScheduledValues(ctx.currentTime);
    masterGainRef.current.gain.linearRampToValueAtTime(0.32, ctx.currentTime + 1.6);
  }, [getCtx]);

  const setMuted = useCallback((muted) => {
    mutedRef.current = muted;
    if (!masterGainRef.current) return;
    const ctx = getCtx();
    masterGainRef.current.gain.cancelScheduledValues(ctx.currentTime);
    masterGainRef.current.gain.linearRampToValueAtTime(muted ? 0 : 0.32, ctx.currentTime + 0.35);
  }, [getCtx]);

  return { startAmbient, playFeatherPlace, dipAmbient, restoreAmbient, setMuted };
}
